<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <v-card-title class="screen-title">
        イントラネットFW ポリシー設定
      </v-card-title>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="savePolicy"
      >
        ×
      </button>
    </template>
    <template v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="font-weight-bold mt-3">
            ゾーン一覧画面で、反映ボタンを押さないと機器には反映されません
          </div>
          {{ zoneRelationToDisplay }}
          <v-container fluid class="pa-1">
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ intranetFwPolicy.policyList.length }}件を表示</span>
              <v-btn
                color="primary"
                v-permission.config
                @click="showPolicyRuleEdit"
              >
                ポリシー編集
              </v-btn>
            </v-row>
            <app-table
              density="compact"
              :fields="policyTable.fields"
              :items="intranetFwPolicy.policyList"
              :per-page="-1"
              primary-key="policyId"
              :td-class="tdClass"
              :tr-class="policyRowClass"
            >
              <!-- 送信元アドレス -->
              <template #cell(srcAddress)="{ item }">
                <intranet-policy-src-address
                  :ip-address-list="item.srcAddressList"
                />
              </template>

              <!-- 宛先アドレス -->
              <template #cell(dstAddress)="{ item }">
                <intranet-policy-dst-address
                  :ip-address-list="item.dstAddressList"
                />
              </template>

              <!-- サービス -->
              <template #cell(service)="{ item }">
                <intranet-policy-service
                  :custom-service-list="item.customServiceList"
                  :default-service-list="item.serviceList"
                />
              </template>

              <!-- アクション -->
              <template #cell(actionType)="{ value }">
                {{ $filter.enumTo(value, "allow_deny") }}
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="outlined" color="primary" @click="savePolicy">
              戻る
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type {
  IntranetFwPolicy,
  IntraPolicy,
  ZonePolicyInfo,
} from "@/apis/IntranetfwPolicyApi";
import type { IntranetfwServiceMenu } from "@/apis/IntranetfwServiceMenuApi";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader } from "@/components/AppTable.vue";
import IntranetPolicySrcAddress from "@/components/policy/IntranetPolicySrcAddress.vue";
import IntranetPolicyDstAddress from "@/components/policy/IntranetPolicyDstAddress.vue";
import IntranetPolicyService from "@/components/policy/IntranetPolicyService.vue";
import IntranetFwPolicyRuleViewRD from "@/modals/applianceContractSetting/IntranetFirewall/policy/ifw/IntranetFwPolicyRuleViewRD.vue";

export default defineComponent({
  name: "IntranetFwPolicyList",
  props: {
    /** 遷移元で選択したゾーン接続 */
    zoneRelationItem: {
      type: Object as PropType<ZoneRelationItem>,
      required: true,
    },
    /** イントラネットFWポリシー */
    policy: {
      type: Object as PropType<IntranetFwPolicy>,
      required: true,
    },
    /** イントラネット契約情報(設定反映の表示用) */
    contractInfo: {
      type: Object as PropType<
        Pick<IntranetfwServiceMenu, "vpnCode" | "intranetFwBandwidth">
      >,
      required: true,
    },
  },
  components: {
    IntranetPolicySrcAddress,
    IntranetPolicyDstAddress,
    IntranetPolicyService,
  },
  data() {
    const clonePolicy = cloneDeep(this.policy);
    /** 該当のリレーションIDを持つポリシー情報が存在しない場合はポリシー0件のオブジェクトを作成 */
    const createEmptyPolicyInfo = (
      list: ZonePolicyInfo[]
    ): ZonePolicyInfo[] => {
      if (
        !list.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )
      ) {
        return [
          ...list,
          {
            zoneRelationId: this.zoneRelationItem.zoneRelationId,
            total: 0,
            policyList: [],
          },
        ];
      } else {
        return list;
      }
    };
    const srcZoneType = this.zoneRelationItem.srcZoneType;
    const dstZoneType = this.zoneRelationItem.dstZoneType;
    if (srcZoneType === "PRIVATE" && dstZoneType === "PRIVATE") {
      clonePolicy.privateToPrivateList = createEmptyPolicyInfo(
        clonePolicy.privateToPrivateList
      );
    } else if (srcZoneType === "EXTRA" && dstZoneType === "PRIVATE") {
      clonePolicy.extraToPrivateList = createEmptyPolicyInfo(
        clonePolicy.extraToPrivateList
      );
    } else if (srcZoneType === "PRIVATE" && dstZoneType === "EXTRA") {
      clonePolicy.privateToExtraList = createEmptyPolicyInfo(
        clonePolicy.privateToExtraList
      );
    }

    return {
      clonePolicy,
      policyTable: {
        fields: [
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddress", title: "送信元アドレス", width: "150px" },
          { key: "dstAddress", title: "宛先アドレス", width: "150px" },
          { key: "service", title: "サービス", width: "15%" },
          { key: "actionType", title: "アクション", width: "80px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
        ],
      },
    };
  },
  computed: {
    /** ゾーン接続方向の表示情報 */
    zoneRelationToDisplay(): string {
      return (
        this.$filter.enumTo(this.zoneRelationItem.srcZoneType, "zoneType") +
        "/" +
        this.zoneRelationItem.srcZoneName +
        " → " +
        this.$filter.enumTo(this.zoneRelationItem.dstZoneType, "zoneType") +
        "/" +
        this.zoneRelationItem.dstZoneName
      );
    },
    /** 選択されたゾーンのポリシーリストを返却する */
    intranetFwPolicy(): ZonePolicyInfo {
      if (
        this.zoneRelationItem.srcZoneType === "PRIVATE" &&
        this.zoneRelationItem.dstZoneType === "PRIVATE"
      ) {
        // 遷移元で選択した行がプライベートゾーンからプライベートゾーンの場合
        return this.clonePolicy.privateToPrivateList.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )!;
      } else if (
        this.zoneRelationItem.srcZoneType === "EXTRA" &&
        this.zoneRelationItem.dstZoneType === "PRIVATE"
      ) {
        // 遷移元で選択した行がエクストラゾーンからプライベートゾーンの場合
        return this.clonePolicy.extraToPrivateList.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )!;
      } else if (
        this.zoneRelationItem.srcZoneType === "PRIVATE" &&
        this.zoneRelationItem.dstZoneType === "EXTRA"
      ) {
        // 遷移元で選択した行がプライベートゾーンからエクストラゾーンの場合
        return this.clonePolicy.privateToExtraList.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )!;
      } else {
        // インターネットゾーンやエクストラとエクストラの組み合わせは存在しない想定
        throw new Error(
          "supported only `PRIVATE → PRIVATE`, `EXTRA → PRIVATE`, `PRIVATE → EXTRA`"
        );
      }
    },
  },
  methods: {
    tdClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /**
     * ポリシーテーブルのtrクラス.
     * 編集不可デフォルトポリシーの場合は特定のクラスを返す
     */
    policyRowClass(
      item: Pick<IntraPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      return item.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY"
        ? ["gray-out"]
        : undefined;
    },
    /** ポリシー編集モーダルを表示 */
    async showPolicyRuleEdit() {
      // ポリシー編集モーダルの表示 ポリシー編集で変更した内容を受け取り一覧画面に反映
      this.intranetFwPolicy.policyList = await this.$modal.show(
        IntranetFwPolicyRuleViewRD,
        {
          zone: this.zoneRelationItem,
          policyList: this.intranetFwPolicy.policyList,
          intranetPolicy: this.clonePolicy,
          contractInfo: this.contractInfo,
        }
      );
    },
    /**
     * 戻るボタン・×を押下した時の処理
     * モーダルを閉じて遷移元に編集したポリシーを返却する
     */
    savePolicy() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.clonePolicy);
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
